'use client'

import { useEffect, useRef, useState } from 'react'

import { GameShell } from '@/components/losi-games/game-shell'

import { mountGameCanvas } from '@/lib/losi-games/canvas'
import { difficultyScale, type Difficulty } from '@/lib/losi-games/difficulty'
import { drawEyeCapsule, drawInkDot } from '@/lib/losi-games/glyphs'
import { bestLocalScore, recordScoreAndWhisper } from '@/lib/losi-games/leaderboard'
import { resolveGameTheme } from '@/lib/losi-games/theme'
import type { LosiGameRankWhisper } from '@/lib/losi-games/types'

type Brick = {
  x: number
  y: number
  halfW: number
  halfH: number
  hp: number
  kind: 'eye' | 'barrier'
}
type Spinner = { x: number; y: number; halfH: number; halfW: number; ang: number; spin: number }
type Ball = { x: number; y: number; vx: number; vy: number; stuck: boolean }
type PowerKind = 'multi' | 'sticky' | 'wide' | 'laser'
type Drop = { x: number; y: number; kind: PowerKind; vy: number }
type Laser = { x: number; y: number; vy: number }

const POWER_KINDS: PowerKind[] = ['multi', 'sticky', 'wide', 'laser']
const BASE_LEN = 44
const BALL_R = 6.5

export function EyeBreakGame({ colorId = 'creme' }: { colorId?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [score, setScore] = useState(0)
  const [best, setBest] = useState(0)
  const [lives, setLives] = useState(3)
  const [wave, setWave] = useState(1)
  const [menu, setMenu] = useState(true)
  const [over, setOver] = useState(false)
  const [won, setWon] = useState(false)
  const [buff, setBuff] = useState('')
  const [whisper, setWhisper] = useState<LosiGameRankWhisper | null>(null)
  const [theme, setTheme] = useState(() => resolveGameTheme(colorId))
  const themeRef = useRef(theme)
  const startFn = useRef<(d: Difficulty) => void>()
  const restartFn = useRef<() => void>()

  useEffect(() => {
    const next = resolveGameTheme(colorId)
    themeRef.current = next
    setTheme(next)
  }, [colorId])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    themeRef.current = resolveGameTheme(colorId)
    let cleanup: (() => void) | undefined

  setBest(bestLocalScore('eye-break'))
  const { ctx, size, dispose } = mountGameCanvas(canvas)

  let { w, h } = size()
  const pad = { x: w / 2, y: h - 28, len: BASE_LEN, thick: 9 }
  const balls: Ball[] = []
  const bricks: Brick[] = []
  const barriers: Brick[] = []
  const spinners: Spinner[] = []
  const drops: Drop[] = []
  const lasers: Laser[] = []
  let scoreAcc = 0
  let lifeAcc = 3
  let waveAcc = 1
  let gameOver = false
  let playing = false
  let diffScale = 1
  let dropChance = 0.22
  let stickyT = 0
  let wideT = 0
  let laserT = 0
  let laserCd = 0
  let last = performance.now()
  let pointerX = w / 2
  let alive = true
  let raf = 0

  const syncBuff = () => {
    const bits: string[] = []
    if (stickyT > 0) bits.push('sticky')
    if (wideT > 0) bits.push('wide')
    if (laserT > 0) bits.push('laser')
    if (balls.length > 1) bits.push(`×${balls.length}`)
    setBuff(bits.join(' · '))
  }

  const clearField = () => {
    bricks.length = 0
    barriers.length = 0
    spinners.length = 0
    drops.length = 0
    lasers.length = 0
  }

  const buildWave = (n: number) => {
    clearField()
    ;({ w, h } = size())
    const cols = 7
    const rows = 3 + Math.min(4, n - 1)
    const gapX = 8
    const gapY = 10
    const halfW = (w - 48 - gapX * (cols - 1)) / cols / 2
    const halfH = 11
    const startY = 56
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        bricks.push({
          x: 24 + halfW + c * (halfW * 2 + gapX),
          y: startY + r * (halfH * 2 + gapY),
          halfW,
          halfH,
          hp: r === 0 && n >= 3 ? 2 : 1,
          kind: 'eye'
        })
      }
    }
    if (n >= 2) {
      barriers.push(
        { x: w * 0.28, y: h * 0.48, halfW: 8, halfH: 36, hp: 999, kind: 'barrier' },
        { x: w * 0.72, y: h * 0.48, halfW: 8, halfH: 36, hp: 999, kind: 'barrier' }
      )
    }
    if (n >= 3) {
      const count = Math.min(4, 1 + (n - 3))
      for (let i = 0; i < count; i++) {
        spinners.push({
          x: w * (0.35 + (i % 2) * 0.3),
          y: h * (0.38 + Math.floor(i / 2) * 0.12),
          halfH: 18,
          halfW: 7,
          ang: (i * Math.PI) / 3,
          spin: (1.6 + i * 0.35) * (i % 2 === 0 ? 1 : -1)
        })
      }
    }
  }

  const resetBalls = (stick: boolean) => {
    ;({ w, h } = size())
    pad.y = h - 28
    balls.length = 0
    balls.push({
      x: pad.x,
      y: pad.y - pad.thick - BALL_R - 2,
      vx: 0,
      vy: 0,
      stuck: stick
    })
  }

  const maybeDrop = (x: number, y: number) => {
    if (Math.random() > dropChance) return
    drops.push({
      x,
      y,
      kind: POWER_KINDS[(Math.random() * POWER_KINDS.length) | 0]!,
      vy: 90 + Math.random() * 40
    })
  }

  const applyPower = (kind: PowerKind) => {
    if (kind === 'multi') {
      const src = balls[0]
      if (!src || src.stuck) return
      for (const ang of [-0.35, 0.35]) {
        if (balls.length >= 5) break
        balls.push({
          x: src.x,
          y: src.y,
          vx: src.vx * Math.cos(ang) - src.vy * Math.sin(ang),
          vy: src.vx * Math.sin(ang) + src.vy * Math.cos(ang),
          stuck: false
        })
      }
    } else if (kind === 'sticky') stickyT = 8
    else if (kind === 'wide') {
      wideT = 10
      pad.len = BASE_LEN * 1.55
    } else if (kind === 'laser') laserT = 8
    syncBuff()
  }

  const endRun = (didWin: boolean) => {
    if (gameOver) return
    gameOver = true
    playing = false
    setOver(!didWin)
    setWon(didWin)
    setWhisper(recordScoreAndWhisper('eye-break', scoreAcc))
    setBest(bestLocalScore('eye-break'))
  }

  startFn.current = (d: Difficulty) => {
    diffScale = difficultyScale(d)
    dropChance = d === 'easy' ? 0.32 : d === 'hard' ? 0.14 : 0.22
    scoreAcc = 0
    setScore(0)
    lifeAcc = d === 'easy' ? 5 : d === 'hard' ? 2 : 3
    setLives(lifeAcc)
    waveAcc = 1
    setWave(1)
    stickyT = wideT = laserT = 0
    pad.len = BASE_LEN
    setBuff('')
    gameOver = false
    setOver(false)
    setWon(false)
    setMenu(false)
    setWhisper(null)
    playing = true
    buildWave(1)
    resetBalls(true)
  }

  restartFn.current = () => {
    setMenu(true)
    setOver(false)
    setWon(false)
    playing = false
    setBuff('')
    setBest(bestLocalScore('eye-break'))
  }

  const hitBallCapsule = (
    ball: Ball,
    cx: number,
    cy: number,
    halfW: number,
    halfH: number,
    ang = 0
  ): boolean => {
    const dx = ball.x - cx
    const dy = ball.y - cy
    const ca = Math.cos(-ang)
    const sa = Math.sin(-ang)
    const lx = dx * ca - dy * sa
    const ly = dx * sa + dy * ca
    const closestX = Math.max(-halfW, Math.min(halfW, lx))
    const closestY = Math.max(-halfH, Math.min(halfH, ly))
    const ox = lx - closestX
    const oy = ly - closestY
    if (ox * ox + oy * oy > BALL_R * BALL_R) return false
    let nx = ox
    let ny = oy
    const len = Math.hypot(nx, ny) || 1
    nx /= len
    ny /= len
    const wx = nx * ca + ny * sa
    const wy = -nx * sa + ny * ca
    ball.x += wx * 2
    ball.y += wy * 2
    const dot = ball.vx * wx + ball.vy * wy
    ball.vx = (ball.vx - 2.05 * dot * wx) * 1.01
    ball.vy = (ball.vy - 2.05 * dot * wy) * 1.01
    return true
  }

  const onMove = (e: PointerEvent) => {
    const rect = canvas.getBoundingClientRect()
    pointerX = e.clientX - rect.left
  }

  const onDown = () => {
    if (!playing || gameOver) return
    let launched = false
    for (const b of balls) {
      if (b.stuck) {
        b.stuck = false
        b.vx = 160 * diffScale * (Math.random() > 0.5 ? 1 : -1)
        b.vy = -280 * diffScale
        launched = true
      }
    }
    if (!launched && laserT > 0 && laserCd <= 0) {
      lasers.push({ x: pad.x - 10, y: pad.y - pad.thick, vy: -520 })
      lasers.push({ x: pad.x + 10, y: pad.y - pad.thick, vy: -520 })
      laserCd = 0.18
    }
  }
  canvas.addEventListener('pointermove', onMove)
  canvas.addEventListener('pointerdown', onDown)

  const loop = (now: number) => {
    if (!alive) return
    const dt = Math.min(0.033, (now - last) / 1000)
    last = now
    ;({ w, h } = size())
    const th = themeRef.current

    if (playing && !gameOver) {
      stickyT = Math.max(0, stickyT - dt)
      wideT = Math.max(0, wideT - dt)
      laserT = Math.max(0, laserT - dt)
      laserCd = Math.max(0, laserCd - dt)
      if (wideT <= 0) pad.len = BASE_LEN
      syncBuff()

      pad.x += (pointerX - pad.x) * Math.min(1, dt * 14)
      pad.x = Math.max(pad.len + 8, Math.min(w - pad.len - 8, pad.x))
      pad.y = h - 28

      for (const ball of balls) {
        if (ball.stuck) {
          ball.x = pad.x
          ball.y = pad.y - pad.thick - BALL_R - 2
          continue
        }
        ball.x += ball.vx * dt
        ball.y += ball.vy * dt
        const sp = Math.hypot(ball.vx, ball.vy)
        const cap = (420 + waveAcc * 18) * diffScale
        if (sp > cap) {
          ball.vx = (ball.vx / sp) * cap
          ball.vy = (ball.vy / sp) * cap
        }
        if (ball.x < BALL_R) {
          ball.x = BALL_R
          ball.vx = Math.abs(ball.vx)
        }
        if (ball.x > w - BALL_R) {
          ball.x = w - BALL_R
          ball.vx = -Math.abs(ball.vx)
        }
        if (ball.y < BALL_R) {
          ball.y = BALL_R
          ball.vy = Math.abs(ball.vy)
        }
        if (ball.vy > 0 && hitBallCapsule(ball, pad.x, pad.y, pad.thick, pad.len, Math.PI / 2)) {
          if (stickyT > 0) {
            ball.stuck = true
            ball.vx = 0
            ball.vy = 0
          } else {
            const offset = (ball.x - pad.x) / pad.len
            ball.vx += offset * 120
            ball.vy = -Math.abs(ball.vy)
          }
        }
        for (let i = bricks.length - 1; i >= 0; i--) {
          const b = bricks[i]!
          if (hitBallCapsule(ball, b.x, b.y, b.halfW, b.halfH, 0)) {
            b.hp -= 1
            if (b.hp <= 0) {
              bricks.splice(i, 1)
              scoreAcc += 10
              maybeDrop(b.x, b.y)
            } else scoreAcc += 3
            setScore(scoreAcc)
          }
        }
        for (const b of barriers) hitBallCapsule(ball, b.x, b.y, b.halfW, b.halfH, 0)
        for (const s of spinners) {
          if (hitBallCapsule(ball, s.x, s.y, s.halfW, s.halfH, s.ang)) {
            ball.vx += Math.cos(s.ang) * s.spin * 18
            ball.vy += Math.sin(s.ang) * s.spin * 18
            scoreAcc += 2
            setScore(scoreAcc)
          }
        }
      }

      for (const s of spinners) s.ang += s.spin * dt * diffScale

      for (let i = balls.length - 1; i >= 0; i--) {
        if (!balls[i]!.stuck && balls[i]!.y > h + 30) balls.splice(i, 1)
      }
      if (balls.length === 0) {
        lifeAcc -= 1
        setLives(lifeAcc)
        if (lifeAcc <= 0) endRun(false)
        else resetBalls(true)
      }

      for (const d of drops) d.y += d.vy * dt
      for (let i = drops.length - 1; i >= 0; i--) {
        const d = drops[i]!
        if (d.y > h + 20) {
          drops.splice(i, 1)
          continue
        }
        if (Math.abs(d.x - pad.x) < pad.len + 8 && Math.abs(d.y - pad.y) < pad.thick + 10) {
          applyPower(d.kind)
          scoreAcc += 5
          setScore(scoreAcc)
          drops.splice(i, 1)
        }
      }

      for (const L of lasers) L.y += L.vy * dt
      for (let i = lasers.length - 1; i >= 0; i--) {
        const L = lasers[i]!
        if (L.y < -10) {
          lasers.splice(i, 1)
          continue
        }
        let hit = false
        for (let j = bricks.length - 1; j >= 0; j--) {
          const b = bricks[j]!
          if (Math.abs(L.x - b.x) < b.halfW + 3 && Math.abs(L.y - b.y) < b.halfH + 4) {
            b.hp -= 1
            if (b.hp <= 0) {
              bricks.splice(j, 1)
              scoreAcc += 10
              maybeDrop(b.x, b.y)
            } else scoreAcc += 3
            setScore(scoreAcc)
            hit = true
            break
          }
        }
        if (hit) lasers.splice(i, 1)
      }

      if (!gameOver && bricks.length === 0) {
        scoreAcc += waveAcc >= 6 ? 70 : 50
        setScore(scoreAcc)
        waveAcc += 1
        setWave(waveAcc)
        stickyT = wideT = laserT = 0
        pad.len = BASE_LEN
        buildWave(waveAcc)
        resetBalls(true)
      }
    }

    ctx.fillStyle = th.paper
    ctx.fillRect(0, 0, w, h)
    for (const b of bricks) {
      drawEyeCapsule(ctx, b.x, b.y, b.halfH, b.halfW, 0, b.hp > 1 ? th.mark : th.body)
    }
    for (const b of barriers) {
      ctx.save()
      ctx.globalAlpha = 0.55
      drawEyeCapsule(ctx, b.x, b.y, b.halfH, b.halfW, 0, th.body)
      ctx.restore()
    }
    for (const s of spinners) drawEyeCapsule(ctx, s.x, s.y, s.halfH, s.halfW, s.ang, th.body)
    for (const d of drops) {
      drawEyeCapsule(ctx, d.x, d.y, 10, 5, 0, th.mark)
      ctx.fillStyle = th.paperDark ? '#fff' : '#0a0a0c'
      ctx.font = '9px ui-sans-serif, system-ui, sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(d.kind[0]!.toUpperCase(), d.x, d.y)
    }
    for (const L of lasers) {
      ctx.strokeStyle = th.mark
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(L.x, L.y)
      ctx.lineTo(L.x, L.y + 14)
      ctx.stroke()
    }
    drawEyeCapsule(ctx, pad.x, pad.y, pad.len, pad.thick, Math.PI / 2, th.body)
    if (playing && !gameOver) {
      for (const b of balls) drawInkDot(ctx, b.x, b.y, BALL_R, th.mark)
    }

    raf = requestAnimationFrame(loop)
  }
  raf = requestAnimationFrame(loop)
  cleanup = () => {
    alive = false
    cancelAnimationFrame(raf)
    dispose()
    canvas.removeEventListener('pointermove', onMove)
    canvas.removeEventListener('pointerdown', onDown)
  }

    return () => {
      cleanup?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <GameShell
      theme={theme}
      title="Breakout"
      score={score}
      best={best}
      meta={buff ? `${buff} · ♥${lives} · wave ${wave}` : `♥${lives} · wave ${wave}`}
      menu={menu}
      over={over}
      won={won}
      whisper={whisper}
      hint="Catch drops: M multiball · S sticky · W wide · L laser. Waves keep stacking."
      onStart={(d) => startFn.current?.(d)}
      onRestart={() => restartFn.current?.()}
    >
      <canvas ref={canvasRef} className="block touch-none" />
    </GameShell>
  )
}
